import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import "@/styles/chrisLp3.css";

// CHRIS · LP3 WAITING EXPERIENCE v1 (2026-09-27)
// IMPLEMENTED / TECHNICALLY VERIFIED / AWAITING OPERATOR LIVE APPROVAL — NICHT FROZEN.
//
// Chris begleitet die LP3-Wartephase nach dem Absenden der Landingpage-Eingabe.
// Diese Komponente ist eine eigenständige React-Ebene (LP-C1-`landing-runtime.js`
// wird NICHT kopiert). Sie übernimmt ausschliesslich die bewährten LP-C1-PRINZIPIEN:
//   • Geometrie DOM-relativ + über gemessene Alpha-Bounds der Pose (kein Raten).
//   • Kein Viewport-Sticker: Figur/Bubble liegen IN-FLOW in der Stage. Scrollt die
//     Seite, scrollen sie natürlich mit und dürfen offscreen laufen — ganz ohne
//     Scroll-Listener, ohne Top-Floor (Offscreen-Vertrag §6 durch Dokumentfluss erfüllt).
//   • Resize = Single Source of Truth über repositionRef (§10).
//   • Reduced Motion (§12): keine Bewegungs-Tweens, gleiche Semantik/Geometrie.
//   • Pointer-Events der Assistenzebene = none (§9).
// Nur FINAL FROZEN v1.1-Assets aus /chris/v1_1/production/ (§1). Keine Bildbearbeitung.

const ASSET = "/chris/v1_1/production/";
const RATIO = 1024 / 1536;

// Gemessene sichtbare Alpha-Bounds (Anteile des 1024×1536-Canvas, alpha>16).
// visH/visW = sichtbare Höhe/Breite · cx = sichtbarer horizontaler Mittelpunkt ·
// bottom = sichtbare Unterkante (Fusslinie) als Anteil von oben. Quelle: PIL-Messung 27.09.2026.
export const POSES = {
  intro: { src: ASSET + "21_chris_intro_greeting.png", visH: 0.9460, visW: 0.7012, cx: 0.5005, bottom: 0.9727 },
  present: { src: ASSET + "23_chris_present_explain.png", visH: 0.9460, visW: 0.9004, cx: 0.4995, bottom: 0.9707 },
  think: { src: ASSET + "22_chris_think_check.png", visH: 0.9434, visW: 0.6143, cx: 0.5020, bottom: 0.9746 },
};

// Verbindliche Betreiber-Inhalte (exakt) — Abschnitt 1 (allgemein) + Abschnitt 2 (eingabebezogen).
export const STEPS_A = [
  "Tätigkeit verstanden",
  "Gefährdungen werden ermittelt",
  "Unterweisung wird erstellt",
  "Betriebsanweisung wird vorbereitet",
  "Prüffristen werden ermittelt",
  "Nachweise werden vorbereitet",
  "Dokumentation wird fertiggestellt",
];
export function stepsB(term) {
  const t = (term || "").trim();
  return [
    `Gefährdungsbeurteilung: ${t}`,
    `Unterweisung: ${t}`,
    `Betriebsanweisung: ${t}`,
    "Prüffristen und Wiederholungen werden vorbereitet",
    "Nachweise und Prüfmappe werden vorbereitet",
  ];
}

const INTRO_TEXT =
  "Ich lege direkt los! Ich bereite jetzt Ihre komplette Arbeitsschutz-Dokumentation vor und zeige Ihnen, was dabei entsteht.";
const FINAL_TEXT = "Geschafft! Ihre Dokumentation ist bereit. Weiter geht es zu Ihrer Dokumentation.";
const WAIT_LABEL = "Dokumentation wird fertiggestellt …";

// Zeitarchitektur (~60 s ruhige Präsentation; NIE ein Backend-Blocker).
const D = {
  silence: 2500, introEnter: 700, introSpeech: 3600, toProgress: 850,
  stepA: 4000, swapGap: 850, stepB: 4000, catchup: 900,
  endGapA: 600, endGapB: 500, finalEnter: 700, finalSpeech: 2600,
};
const D_REDUCED = {
  ...D, silence: 900, introEnter: 0, toProgress: 0, swapGap: 200, endGapA: 200, endGapB: 200, finalEnter: 0,
};

function visHFor(w) {
  if (w >= 1280) return 340;
  if (w >= 768) return 300;
  if (w >= 480) return 200;
  return 175;
}

// Reine, testbare Geometrie: liefert die PNG-Box + sichtbare On-Screen-Breite,
// sodass die SICHTBARE Fusslinie/Center exakt auf den Anker gelegt werden kann.
export function computeFigureBox(visHpx, pose) {
  const Himg = visHpx / pose.visH;
  const Wimg = Himg * RATIO;
  return { Himg, Wimg, visWpx: pose.visW * Wimg, cxOffset: pose.cx * Wimg, bottomOffset: pose.bottom * Himg };
}

function detectReduced() {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return false;
  }
}

export default function ChrisLp3Waiting({ term, ready, onHandoff, forceReduced = false, freezeState = null, freezeStage = "A", freezeRevealed = 0, debugBox = false }) {
  const reduced = forceReduced || detectReduced();
  const dur = reduced ? D_REDUCED : D;
  const frozen = !!freezeState; // DEV-ONLY: rendert einen Zustand deterministisch (Timer aus)

  const [mstate, setMstate] = useState(freezeState || "silence");
  const [stageAB, setStageAB] = useState(freezeState ? freezeStage : "A");
  const [revealed, setRevealed] = useState(freezeState ? freezeRevealed : 0);

  const readyRef = useRef(ready);
  useEffect(() => { readyRef.current = ready; }, [ready]);

  const handedOff = useRef(false);
  const timers = useRef([]);
  const pushT = (id) => { timers.current.push(id); return id; };
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current = []; }, []);

  const stageRef = useRef(null);
  const figRef = useRef(null);
  const bubbleRef = useRef(null);
  const cardRef = useRef(null);

  // ---- Ableitungen aus dem Maschinenzustand ----
  const layoutMode = (
    mstate === "toProgress" || mstate === "progressA" || mstate === "swap" ||
    mstate === "progressB" || mstate === "waitBackend"
  ) ? "progress" : "center";
  const visible = mstate !== "silence";
  const poseKey = mstate === "waitBackend"
    ? "think"
    : (mstate === "introEnter" || mstate === "introSpeech") ? "intro" : "present";
  const bubbleText = mstate === "introSpeech" ? INTRO_TEXT : mstate === "finalSpeech" ? FINAL_TEXT : null;

  const list = stageAB === "A" ? STEPS_A : stepsB(term);
  const showCard = layoutMode === "progress";

  const rows = useMemo(() => {
    if (mstate === "waitBackend") {
      const done = stepsB(term).map((label) => ({ label, state: "done" }));
      done.push({ label: WAIT_LABEL, state: "active" });
      return done;
    }
    if (!showCard) return [];
    return list.map((label, i) => ({
      label,
      state: i < revealed ? "done" : i === revealed ? "active" : "pending",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mstate, stageAB, revealed, term, showCard]);

  const activeLabel = useMemo(() => {
    if (mstate === "finalSpeech") return "Ihre Dokumentation ist bereit.";
    if (mstate === "waitBackend") return "Dokumentation wird fertiggestellt.";
    if (showCard) {
      const active = rows.find((r) => r.state === "active");
      return active ? active.label : "";
    }
    return "";
  }, [mstate, showCard, rows]);

  // ---- Maschinen-Timer: lineare Übergänge ----
  useEffect(() => {
    if (frozen) return undefined;
    pushT(setTimeout(() => setMstate("introEnter"), dur.silence));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (frozen) return;
    if (mstate === "introEnter") pushT(setTimeout(() => setMstate("introSpeech"), dur.introEnter));
    else if (mstate === "introSpeech") pushT(setTimeout(() => setMstate("toProgress"), dur.introSpeech));
    else if (mstate === "toProgress") { setStageAB("A"); setRevealed(0); pushT(setTimeout(() => setMstate("progressA"), dur.toProgress)); }
    else if (mstate === "swap") { setStageAB("B"); setRevealed(0); pushT(setTimeout(() => setMstate("progressB"), dur.swapGap)); }
    else if (mstate === "finalEnter") pushT(setTimeout(() => setMstate("finalSpeech"), dur.finalEnter));
    else if (mstate === "finalSpeech") pushT(setTimeout(() => setMstate("done"), dur.finalSpeech));
    else if (mstate === "done") { if (!handedOff.current) { handedOff.current = true; onHandoff && onHandoff(); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mstate]);

  // Schritt-Enthüllung (Intervall). Sobald das Backend fertig ist, greift ein
  // ruhiger Catch-up (kein hektisches 1-s-Durchrattern, kein 60-s-Zwang).
  useEffect(() => {
    if (frozen) return undefined;
    if (mstate !== "progressA" && mstate !== "progressB") return undefined;
    const total = mstate === "progressA" ? STEPS_A.length : stepsB(term).length;
    const base = mstate === "progressA" ? dur.stepA : dur.stepB;
    const interval = readyRef.current ? dur.catchup : base;
    const iv = setInterval(() => {
      setRevealed((prev) => {
        if (prev >= total) { clearInterval(iv); return prev; }
        return prev + 1;
      });
    }, interval);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mstate, ready, term]);

  // Stufen-Abschluss: wenn alle Schritte der aktuellen Stufe gesetzt sind.
  useEffect(() => {
    if (frozen) return undefined;
    if (mstate !== "progressA" && mstate !== "progressB") return undefined;
    const total = mstate === "progressA" ? STEPS_A.length : stepsB(term).length;
    if (revealed < total) return undefined;
    let id;
    if (mstate === "progressA") id = setTimeout(() => setMstate("swap"), dur.endGapA);
    else id = setTimeout(() => setMstate(readyRef.current ? "finalEnter" : "waitBackend"), dur.endGapB);
    pushT(id);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mstate, revealed, term]);

  // Late-Ready: Präsentation fertig, Backend erst danach → ruhig warten (Pose 22),
  // bei echtem Ready in die Finalphase. KEINE Fake-Prozente, keine Wiederhol-Bubbles.
  useEffect(() => {
    if (frozen) return;
    if (ready && mstate === "waitBackend") setMstate("finalEnter");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mstate]);

  // ---- Reposition (DOM-relativ + gemessene Alpha-Bounds) ----
  const reposition = useCallback(() => {
    const stage = stageRef.current;
    const fig = figRef.current;
    if (!stage || !fig) return;
    const sr = stage.getBoundingClientRect();
    const w = sr.width || window.innerWidth;
    const h = sr.height || window.innerHeight;
    const desktop = w >= 768;
    const visHpx = visHFor(w);
    const pose = POSES[poseKey] || POSES.intro;
    const box = computeFigureBox(visHpx, pose);

    let ax; // sichtbarer Fuss-/Center-X (relativ zur Stage)
    let feetY; // sichtbare Fusslinie-Y (relativ zur Stage)
    if (layoutMode === "progress") {
      const card = cardRef.current;
      const cr = card ? card.getBoundingClientRect() : null;
      if (desktop) {
        feetY = h * 0.9;
        if (cr) { const cardLeft = cr.left - sr.left; ax = Math.max(box.visWpx / 2 + 12, cardLeft / 2); }
        else ax = w * 0.27;
      } else {
        ax = w / 2;
        feetY = cr ? Math.max(visHpx * 0.55, (cr.top - sr.top) - 14) : Math.min(h * 0.45, 300);
      }
    } else {
      ax = w / 2;
      feetY = desktop ? h * 0.9 : Math.min(h * 0.5, 290);
    }
    // Horizontale Sicherheits-Clamp (erlaubt §6/§11). KEIN vertikaler Top-Floor.
    ax = Math.max(box.visWpx / 2 + 8, Math.min(ax, w - box.visWpx / 2 - 8));

    fig.style.height = box.Himg + "px";
    fig.style.width = box.Wimg + "px";
    fig.style.left = (ax - box.cxOffset) + "px";
    fig.style.top = (feetY - box.bottomOffset) + "px";

    const bub = bubbleRef.current;
    if (bub) {
      const headY = feetY - visHpx;
      const half = Math.min(170, w * 0.42);
      const bx = Math.max(half + 8, Math.min(ax, w - half - 8));
      bub.style.left = bx + "px";
      bub.style.bottom = (h - (headY - 12)) + "px";
    }
  }, [poseKey, layoutMode]);

  const repositionRef = useRef(reposition);
  repositionRef.current = reposition;

  useLayoutEffect(() => { reposition(); }, [reposition, mstate, visible, revealed, stageAB, ready]);

  useEffect(() => {
    let raf = 0;
    const run = () => { if (raf) cancelAnimationFrame(raf); raf = requestAnimationFrame(() => repositionRef.current()); };
    const onResize = () => {
      const stage = stageRef.current;
      if (stage) stage.classList.add("lp3wait--noanim");
      repositionRef.current();
      requestAnimationFrame(() => requestAnimationFrame(() => { if (stage) stage.classList.remove("lp3wait--noanim"); }));
    };
    window.addEventListener("resize", onResize, { passive: true });
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && stageRef.current) {
      ro = new ResizeObserver(run);
      ro.observe(stageRef.current);
      if (cardRef.current) ro.observe(cardRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const stageCls = `lp3wait-stage${reduced ? " lp3wait--reduced" : ""}`;
  const pose = POSES[poseKey] || POSES.intro;

  return (
    <section
      ref={stageRef}
      className={stageCls}
      data-testid="lp3-chris-stage"
      data-state={mstate}
      data-mode={layoutMode}
    >
      <div className="lp3wait-term" data-testid="lp4-stage-term">Vorschau für „{term}“</div>

      <img
        ref={figRef}
        className="lp3wait-figure"
        src={pose.src}
        alt="Chris"
        aria-hidden="true"
        loading="eager"
        draggable="false"
        data-testid="lp3-chris-figure"
        data-visible={visible ? "true" : "false"}
        data-pose={poseKey}
        data-debug={debugBox ? "true" : "false"}
      />

      <div
        ref={bubbleRef}
        className="lp3wait-bubble"
        data-testid="lp3-chris-bubble"
        data-show={bubbleText ? "true" : "false"}
        aria-hidden="true"
      >
        {bubbleText}
      </div>

      <div
        ref={cardRef}
        className="lp3wait-progresswrap"
        data-testid="lp3-chris-progress"
        data-show={showCard ? "true" : "false"}
      >
        <div className="lp3wait-title">CLartext bereitet Ihre Dokumentation vor …</div>
        <ul className="lp3wait-steps">
          {rows.map((r, i) => (
            <li key={i} className={`lp3wait-step ${r.state}`} data-testid={`lp3-chris-step-${i}`} data-state={r.state}>
              <span className="ic">
                {r.state === "done" ? <Check size={14} /> : r.state === "active" ? <Loader2 size={14} className="lp3wait-spin" /> : <span className="dotmark" />}
              </span>
              <span className="lbl">{r.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="lp3wait-hint">CLartext arbeitet gerade für Sie – das dauert nur einen Moment.</p>

      <div className="lp3wait-sr" role="status" aria-live="polite" data-testid="lp3-chris-status">{activeLabel}</div>
    </section>
  );
}
